import React, { useMemo, useCallback, forwardRef, useImperativeHandle, useState } from "react";
import { Spin, Empty, Result, Divider } from "antd";
import MessageRenderer from "../../../components/Messages/MessageRenderer";
import { ChatMessage, ChatThreadClient, CommunicationUserIdentifier } from "../../../types/chat";
import { useInfiniteMessages } from "../../../hooks/queries/useInfiniteMessages";
import { useJoinAChatRoom } from "../../../hooks/queries/chat";
import { useGetConfirmedMessageIds } from "../../../hooks/queries/useGetConfirmedMessageIds";
import { useGetReactionsInRoom } from "../../../hooks/queries/useGetReactionsInRoom";
import { useGetPinnedMessageIds } from "../../../hooks/queries/useGetPinnedMessageIds";
import { useSearchParamsV6 } from "../../../hooks/useSearchParamsV6";
import { useVirtualizedMessages } from "../../../hooks/useVirtualizedMessages";
import { useMessageNavigation } from "../../../contexts/MessageNavigationContext";
import { ReplyState } from "../../../utils/replyUtils";
import { formatDateHeader } from "../../../utils/dateUtils";
import { DateDivider } from "../../../components/DateDivider";
import styles from "./MessageList.module.scss";
import { ChevronsDown } from "lucide-react";

interface MessageListProps {
  chatThreadClient?: ChatThreadClient | null;
  threadId?: string;
  currentUserId?: string;
  actualUserId?: string;
  roomType?: string;
  isAdmin?: boolean;
  onReply?: (replyState: ReplyState) => void;
}

export interface MessageListRef {
  scrollToMessage: (messageId: string) => void;
}

const MessageList = forwardRef<MessageListRef, MessageListProps>(
  ({ chatThreadClient, threadId, currentUserId, actualUserId, roomType, isAdmin, onReply }, ref) => {
    // State for highlighted message
    const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

    // Get navigation context
    const { registerScrollHandler } = useMessageNavigation();

    // Get roomId from URL
    const [searchParams] = useSearchParamsV6();
    const roomId = searchParams.get("roomId");

    // Fetch room info to get members
    const { data: roomInfo } = useJoinAChatRoom({ roomId: roomId || "" });
    const members = useMemo(() => roomInfo?.Data?.Members || [], [roomInfo]);

    // Fetch messages với infinite pagination
    const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteMessages(
      chatThreadClient || null,
      threadId,
    );

    // Fetch reactions for all messages in room
    const { data: reactionsData } = useGetReactionsInRoom(roomId || "", !!roomId);

    // Fetch confirmed message IDs for current user in room
    const { data: confirmedData } = useGetConfirmedMessageIds(roomId || "");

    // Fetch pinned message IDs
    const { data: pinnedData } = useGetPinnedMessageIds(roomId || "");

    // Flatten all pages thành single array
    const allMessages = useMemo(() => {
      if (!data?.pages) return [];

      // Azure API trả về:
      // - pages: [newestPage, olderPage, oldestPage]
      // - messages trong mỗi page: [newest, ..., oldest]
      //
      // Để hiển thị đúng (oldest → newest), cần:
      // 1. Đảo ngược pages: [oldestPage, olderPage, newestPage]
      // 2. Đảo ngược messages trong mỗi page: [oldest, ..., newest]
      return [...data.pages]
        .reverse() // Đảo pages: oldestPage → newestPage
        .flatMap((p) => [...(p.messages || [])].reverse()) as ChatMessage[]; // Đảo messages trong mỗi page
    }, [data]);

    // Group messages by date for rendering
    const messageGroups = useMemo(() => {
      const groups: Array<{ type: "divider" | "message"; data: string | ChatMessage; key: string }> = [];
      let lastDate: string | null = null;

      allMessages.forEach((message, index) => {
        const messageDate = new Date(message.createdOn);
        const dateHeader = formatDateHeader(messageDate);

        // Add date divider if date changed
        if (dateHeader !== lastDate) {
          groups.push({
            type: "divider",
            data: dateHeader,
            key: `divider-${dateHeader}-${index}`,
          });
          lastDate = dateHeader;
        }

        // Add message
        groups.push({
          type: "message",
          data: message,
          key: message.id || `message-${index}`,
        });
      });

      return groups;
    }, [allMessages]);

    // Map reactions by messageId for quick lookup
    const reactionsMap = useMemo(() => {
      if (!reactionsData?.Data) return new Map();

      const map = new Map();
      reactionsData.Data.forEach((reaction) => {
        map.set(reaction.MessageId, reaction);
      });
      return map;
    }, [reactionsData]);

    // Create Set of pinned message IDs for quick lookup
    const pinnedMessageIds = useMemo(() => {
      const pinIds = (pinnedData as any)?.Data;
      if (!Array.isArray(pinIds)) return new Set<string>();
      return new Set(pinIds);
    }, [pinnedData]);

    // Create Set of confirmed message IDs for quick lookup
    const confirmedMessageIds = useMemo(() => {
      const ids = confirmedData?.Data;
      if (!Array.isArray(ids)) return new Set<string>();
      return new Set(ids);
    }, [confirmedData]);

    // Scrolling hook
    const { containerRef, scrollToBottom, showScrollButton, unreadCount } = useVirtualizedMessages({
      messages: allMessages,
      threadId, // Thêm threadId để detect khi chuyển phòng
      currentUserId, // Thêm currentUserId để phân biệt tin nhắn của mình
      onLoadMore: fetchNextPage,
      hasMore: hasNextPage,
      isLoading: isFetchingNextPage,
    });

    // Expose scrollToMessage method via ref and reusable callback
    const handleScrollToMessage = useCallback(
      (messageId: string) => {
        if (!containerRef.current) return;

        // Find message element
        const messageElements = containerRef.current.querySelectorAll("[data-message-id]");
        const targetElement = Array.from(messageElements).find(
          (el) => el.getAttribute("data-message-id") === messageId,
        );

        if (targetElement) {
          // Scroll to message
          targetElement.scrollIntoView({ behavior: "smooth", block: "center" });

          // Highlight message temporarily
          setHighlightedMessageId(messageId);
          setTimeout(() => setHighlightedMessageId(null), 2000);
        } else {
          console.warn(`Message ${messageId} not found in DOM`);
        }
      },
      [containerRef],
    );

    // Register scroll handler with navigation context
    React.useEffect(() => {
      registerScrollHandler(handleScrollToMessage);
    }, [registerScrollHandler, handleScrollToMessage]);

    useImperativeHandle(
      ref,
      () => ({
        scrollToMessage: handleScrollToMessage,
      }),
      [handleScrollToMessage],
    );

    // Helper function để xác định tin nhắn có phải của mình không
    const isOwnMessage = useCallback(
      (message: ChatMessage) => {
        if (!currentUserId) {
          return false;
        }

        // Check 1: senderCommunicationIdentifier (runtime is STRING despite type definition)
        if ("senderCommunicationIdentifier" in message && message.senderCommunicationIdentifier) {
          // Cast to any because runtime value is string but type says object
          const senderComm = message.senderCommunicationIdentifier as CommunicationUserIdentifier | string | undefined;

          if (typeof senderComm === "string" && senderComm === currentUserId) {
            return true;
          } else if (typeof senderComm === "object" && senderComm.communicationUser?.id === currentUserId) {
            return true;
          }
        }

        // Check 2: sender.communicationUserId (old module format)
        const msgWithSender = message as ChatMessage & { sender?: { communicationUserId?: string } };
        if ("sender" in message && msgWithSender.sender?.communicationUserId) {
          return msgWithSender.sender.communicationUserId === currentUserId;
        }

        // Check 3: createdBy field
        const msgWithCreatedBy = message as ChatMessage & {
          createdBy?: string | { communicationUser?: { id?: string } };
        };
        if (msgWithCreatedBy.createdBy) {
          const createdBy = msgWithCreatedBy.createdBy;

          if (typeof createdBy === "string" && createdBy === currentUserId) {
            return true;
          } else if (typeof createdBy === "object" && createdBy.communicationUser?.id === currentUserId) {
            return true;
          }
        }

        return false;
      },
      [currentUserId],
    );

    // Helper function để lấy avatar từ members dựa vào CUI
    const getSenderAvatar = useCallback(
      (message: ChatMessage) => {
        let senderId: string | undefined;

        // Extract senderId similar to isOwnMessage logic
        if ("senderCommunicationIdentifier" in message && message.senderCommunicationIdentifier) {
          const senderComm = message.senderCommunicationIdentifier as any;
          if (typeof senderComm === "string") {
            senderId = senderComm;
          } else if (typeof senderComm === "object") {
            senderId = senderComm.communicationUser?.id;
          }
        }

        if (!senderId) {
          const msgWithSender = message as ChatMessage & { sender?: { communicationUserId?: string } };
          if ("sender" in message && msgWithSender.sender?.communicationUserId) {
            senderId = msgWithSender.sender.communicationUserId;
          }
        }

        if (!senderId) {
          const msgWithCreatedBy = message as ChatMessage & {
            createdBy?: string | { communicationUser?: { id?: string } };
          };
          if (msgWithCreatedBy.createdBy) {
            const createdBy = msgWithCreatedBy.createdBy;
            if (typeof createdBy === "string") {
              senderId = createdBy;
            } else if (typeof createdBy === "object") {
              senderId = (createdBy as any).communicationUser?.id;
            }
          }
        }

        if (!senderId) return null;

        const member = members.find((m) => m.CUI === senderId);
        return member?.AvatarUrl || null;
      },
      [members],
    );

    // Loading state
    if (isLoading) {
      return (
        <div className={styles.messageList}>
          <div className={styles.loadingContainer}>
            <Divider>
              <Spin size="small" />
            </Divider>
            <p>Đang tải tin nhắn...</p>
          </div>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className={styles.messageList}>
          <div className={styles.errorContainer}>
            <Result status="error" title="Lỗi khi tải tin nhắn" subTitle={(error as Error).message} />
          </div>
        </div>
      );
    }

    // Empty state
    if (allMessages.length === 0) {
      return (
        <div className={styles.messageList}>
          <div className={styles.emptyContainer}>
            <Empty description="Chưa có tin nhắn nào" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        </div>
      );
    }

    // Main render
    return (
      <div className={styles.messageList}>
        <div className={styles.messagesContainer} ref={containerRef}>
          {/* Load more indicator */}
          {isFetchingNextPage && (
            <div className={styles.loadMoreContainer}>
              <Spin size="small" />
              <span>Đang tải thêm...</span>
            </div>
          )}

          {/* Messages with date dividers */}
          {messageGroups.map((group) => {
            if (group.type === "divider") {
              return <DateDivider key={group.key} date={group.data as string} />;
            }

            // Type guard ensures group.data is ChatMessage here
            const message = group.data as ChatMessage;
            const isOwn = isOwnMessage(message);
            const avatar = getSenderAvatar(message);

            return (
              <div
                key={group.key}
                data-message-id={message.id}
                className={message.id === highlightedMessageId ? styles.highlighted : ""}
              >
                <MessageRenderer
                  message={message}
                  isOwnMessage={isOwn}
                  senderAvatar={avatar}
                  roomId={roomId || undefined}
                  threadId={threadId}
                  onReply={onReply}
                  onScrollToMessage={handleScrollToMessage}
                  reaction={reactionsMap.get(message.id)}
                  isPinned={pinnedMessageIds.has(message.id)}
                  isConfirmed={confirmedMessageIds.has(message.id)}
                  currentUserId={currentUserId}
                  actualUserId={actualUserId}
                  roomType={roomType}
                  isAdmin={isAdmin}
                />
              </div>
            );
          })}
        </div>

        {/* Scroll to bottom button */}
        {showScrollButton && (
          <button className={styles.scrollToBottomButton} onClick={() => scrollToBottom("smooth")}>
            <ChevronsDown size={20} color="#0891b2" strokeWidth={3} />
            {unreadCount > 0 && <span className={styles.unreadBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
          </button>
        )}
      </div>
    );

    //  return <CopilotIframe />;
  },
);

MessageList.displayName = "MessageList";

export default MessageList;
